/**
 * Client-side "stay logged in for X days" preference.
 *
 * Web clients pick 7, 14, or 30 days (default 30) in their settings page.
 * On every successful login we stamp `SESSION_STARTED_AT_KEY` with `Date.now()`.
 * A periodic check (every minute) compares `Date.now()` against
 * `sessionStartedAt + lifetimeDays`; once past, the auth context signs
 * the user out and the route guards bounce them to `/login`.
 *
 * **Security note:** This is a client-side cap that's strictly *shorter*
 * than the server-side max (refresh-token TTL = 30 days, enforced
 * server-side). Picking a shorter value means *the client will be
 * forcibly signed out sooner than the refresh token would expire* —
 * useful on shared computers. It doesn't extend the refresh-token TTL.
 */

const LIFETIME_KEY = 'h3eromap.sessionLifetimeDays';
const STARTED_KEY = 'h3eromap.sessionStartedAt';

export type SessionLifetimeDays = 7 | 14 | 30;

export const SESSION_LIFETIME_OPTIONS: SessionLifetimeDays[] = [7, 14, 30];
export const SESSION_LIFETIME_DEFAULT: SessionLifetimeDays = 30;

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getSessionLifetimeDays(): SessionLifetimeDays {
  const ls = safeLocalStorage();
  if (!ls) return SESSION_LIFETIME_DEFAULT;
  const raw = ls.getItem(LIFETIME_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  if (n === 7 || n === 14 || n === 30) return n;
  return SESSION_LIFETIME_DEFAULT;
}

export function setSessionLifetimeDays(value: SessionLifetimeDays): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(LIFETIME_KEY, String(value));
}

/**
 * Stamp `Date.now()` as the session start. Called by the auth context
 * after every successful login + 2FA verify. NOT called on a silent
 * refresh — refresh extends the *access* token within the existing
 * session; it doesn't restart the lifetime window.
 */
export function markSessionStarted(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(STARTED_KEY, String(Date.now()));
}

export function clearSessionStarted(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.removeItem(STARTED_KEY);
}

export function getSessionStartedAt(): number | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  const raw = ls.getItem(STARTED_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * True iff the session has been alive longer than the configured
 * lifetime. Used by the auth context's periodic check to force a
 * sign-out. Returns false (i.e. "session still valid") when:
 *   - `sessionStartedAt` was never stamped (user not signed in)
 *   - the elapsed time is shorter than the lifetime
 *   - localStorage is unavailable (treat as no-cap fallback)
 */
export function isSessionExpired(now: number = Date.now()): boolean {
  const startedAt = getSessionStartedAt();
  if (startedAt === null) return false;
  const lifetimeMs = getSessionLifetimeDays() * 24 * 60 * 60 * 1000;
  return now - startedAt >= lifetimeMs;
}
